import api from './api';

export interface PDFMetadata {
  author?: string;
  createdDate?: Date;
  modifiedDate?: Date;
  subject?: string;
  keywords?: string;
}

export interface PDFDocument {
  _id: string;
  filename: string;
  title: string;
  description?: string;
  fileUrl: string;
  fileSize: number;
  fileSizeMB?: number;
  mimeType: string;
  category: 'training' | 'policy' | 'documentation' | 'compliance' | 'odos' | 'pws' | 'other';
  tags: string[];
  extractedText: string;
  sections?: PDFSection[];
  pageCount: number;
  uploadedBy: string;
  uploadedAt: Date;
  isPublic: boolean;
  metadata: PDFMetadata;
  downloadCount: number;
  lastDownloadedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface PDFSection {
  id: string;
  sectionNumber: string;
  title: string;
  content: string;
  metadata?: {
    lineCount: number;
    characterCount: number;
    startLine: number;
    endLine: number;
  };
}

const pdfService = {
  // Get all PDFs
  async getPDFs(params?: { category?: string; tags?: string; search?: string }): Promise<PDFDocument[]> {
    const queryString = new URLSearchParams(params as any).toString();
    const response = await api.get(`/pdfs${queryString ? `?${queryString}` : ''}`);
    return response.data.data;
  },

  // Get single PDF by ID
  async getPDFById(id: string): Promise<PDFDocument> {
    const response = await api.get(`/pdfs/${id}`);
    return response.data.data;
  },

  // Download PDF
  async downloadPDF(id: string): Promise<void> {
    window.open(`${api.defaults.baseURL}/pdfs/${id}/download`, '_blank');
  },

  // Update PDF metadata
  async updatePDF(id: string, data: Partial<PDFDocument>): Promise<PDFDocument> {
    const response = await api.put(`/pdfs/${id}`, data);
    return response.data.data;
  },

  // Update PDF extracted text (for editing sections)
  async updatePDFText(id: string, extractedText: string): Promise<PDFDocument> {
    const response = await api.put(`/pdfs/${id}`, { extractedText });
    return response.data.data;
  },

  // Delete PDF (admin only)
  async deletePDF(id: string): Promise<void> {
    await api.delete(`/pdfs/${id}`);
  },

  // Upload new PDF
  async uploadPDF(file: File, metadata: {
    title: string;
    description?: string;
    category: string;
    tags?: string;
    isPublic?: boolean;
  }): Promise<PDFDocument> {
    const formData = new FormData();
    formData.append('pdf', file);
    formData.append('title', metadata.title);
    if (metadata.description) formData.append('description', metadata.description);
    formData.append('category', metadata.category);
    if (metadata.tags) formData.append('tags', metadata.tags);
    if (metadata.isPublic !== undefined) formData.append('isPublic', String(metadata.isPublic));

    const response = await api.post('/pdfs/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },

  // Get sections from PDF document (now stored in database)
  getSections(pdfDoc: PDFDocument): PDFSection[] {
    // If sections are already parsed and stored, use them
    if (pdfDoc.sections && pdfDoc.sections.length > 0) {
      return pdfDoc.sections;
    }
    
    // Fallback: create a single section from extracted text
    return [{
      id: 'section-1',
      sectionNumber: '1',
      title: 'Full Document',
      content: pdfDoc.extractedText,
      metadata: {
        lineCount: pdfDoc.extractedText.split('\n').length,
        characterCount: pdfDoc.extractedText.length,
        startLine: 0,
        endLine: pdfDoc.extractedText.split('\n').length
      }
    }];
  },

  // Update a specific section
  updateSection(sections: PDFSection[], sectionId: string, newContent: string): PDFSection[] {
    return sections.map(section => 
      section.id === sectionId 
        ? { ...section, content: newContent }
        : section
    );
  },

  // Reconstruct full text from sections
  reconstructText(sections: PDFSection[]): string {
    return sections.map(section => {
      return `${section.title}\n${section.content}\n`;
    }).join('\n');
  }
};

export default pdfService;
